// client/src/coms/views/canvas_parts/party/PartyCanvasSurface.js
// The drawing surface of every party game: the real Canvas editor (CanvasRoomView in game mode) on a
// private document for this drawing, with the round's rules layered over it (tools, colours, sizes,
// stroke limits, undo, straight lines, tool roulette, blind, mirror, rotation, clouds, bounded parts).
// After each change the writer's picture (their own layers) and a record of the strokes behind it go to
// the server, which checks both and shows the picture to whoever may watch. The document is kept on this
// device too, so a reload or a trip to another Voxemera area comes back to the same drawing.
import React, { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import CanvasRoomView from '../CanvasRoomView';
import { GamesVisibleContext } from '../../games_parts/gamesVisibility';
import { gameDocumentId, isStraightStroke, normaliseHex, samplePoints, toolFamily } from '../canvasGameMode';
import { BOARD, renderItems, scheduledEntry } from './partyDrawingModel';
import { describePartyError, newActionId, partyApi } from './usePartySession';
import './partyGames.css';

const UPLOAD_DELAY_MS = { live: 300, quiet: 1100 };
const UPLOAD_GAP_MS = { live: 700, quiet: 1500 };
const CLOSED_CODES = new Set(['drawing_closed', 'too_late', 'not_your_drawing', 'unknown_drawing', 'no_game_running', 'session_not_found']);
const TOOL_NAMES = { pen: 'Brush', brush: 'Brush', eraser: 'Eraser', fill: 'Fill', line: 'Line', rect: 'Rectangle', ellipse: 'Ellipse' };

function readNumber(key) {
  try {
    return Number(window.sessionStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
}

function writeNumber(key, value) {
  try {
    window.sessionStorage.setItem(key, String(value));
  } catch {}
}

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Where this writer's picture sits on the board.
export function drawingRect(rules) {
  const b = rules && rules.bounds;
  if (b && b.x1 > b.x0 && b.y1 > b.y0) return { x: b.x0, y: b.y0, w: b.x1 - b.x0, h: b.y1 - b.y0 };
  return { x: 0, y: 0, w: BOARD.w, h: BOARD.h };
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

// The engine exports PNG; send WebP where the browser can make it (smaller), mirrored when the rules say
// the drawing is mirrored (the drawer saw it flipped, so everyone else sees the same).
async function preparePicture(dataUrl, mirror) {
  const image = await loadImage(dataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (mirror) {
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(image, 0, 0);
  const webp = canvas.toDataURL('image/webp', 0.9);
  return webp.startsWith('data:image/webp') ? webp : canvas.toDataURL('image/png');
}

// Older stroke items (a base another player drew before pictures existed) as a transparent picture of
// this writer's part of the board.
function itemsPicture(items, rect) {
  if (!items || !items.length || typeof document === 'undefined') return '';
  const board = document.createElement('canvas');
  board.width = BOARD.w;
  board.height = BOARD.h;
  renderItems(board.getContext('2d'), items, { transparent: true });
  const part = document.createElement('canvas');
  part.width = rect.w;
  part.height = rect.h;
  part.getContext('2d').drawImage(board, rect.x, rect.y, rect.w, rect.h, 0, 0, rect.w, rect.h);
  return part.toDataURL('image/png');
}

// A picture placed on the board, positioned over this writer's part (percentages of the document).
function placed(picture, rect, extra = {}) {
  return {
    key: `${picture.u || 'b'}-${picture.seq || 0}-${picture.x}-${picture.y}`,
    src: picture.src,
    left: (((Number(picture.x) || 0) - rect.x) / rect.w) * 100,
    top: (((Number(picture.y) || 0) - rect.y) / rect.h) * 100,
    width: ((Number(picture.w) || BOARD.w) / rect.w) * 100,
    height: ((Number(picture.h) || BOARD.h) / rect.h) * 100,
    ...extra,
  };
}

function emptyRecord() {
  return { strokes: [], undos: 0, clears: 0 };
}

const PartyCanvasSurface = forwardRef(function PartyCanvasSurface({
  game, drawingId, rules, userId, userName = '', label = 'Drawing', live = false, disabled = false, onionId = '', shared = false,
}, ref) {
  const { store, serverNow, setNotice, sessionId } = game;
  // While another Voxemera area is open the editor is put away (its drawing is kept on this device and
  // was already sent), and comes back when the game is on screen again.
  const onScreen = useContext(GamesVisibleContext);
  const [engineMounted, setEngineMounted] = useState(onScreen);
  const safeRules = useMemo(() => rules || {}, [rules]);
  const rect = useMemo(() => drawingRect(safeRules), [safeRules]);
  const key = `${sessionId}:${drawingId}:${userId}`;
  const docId = useMemo(() => gameDocumentId(key), [key]);
  const reducedMotion = useMemo(prefersReducedMotion, []);
  const visual = useMemo(() => safeRules.visual || [], [safeRules.visual]);
  const rotating = visual.includes('rotate');
  const maxStrokes = Math.max(0, Number(safeRules.maxStrokes) || 0);

  const apiRef = useRef(null);
  const strokeRef = useRef(null);
  const recordRef = useRef(emptyRecord());
  const usedRef = useRef(readNumber(`voxemera:party:${key}:strokes`));
  const seqRef = useRef(readNumber(`voxemera:party:${key}:seq`));
  const upload = useRef({ inflight: null, dirty: false, timer: 0, lastAt: 0, closed: false, sent: false });
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const [strokesUsed, setStrokesUsed] = useState(usedRef.current);
  const [closed, setClosed] = useState(false);
  const [, setTick] = useState(0);
  const [modelVersion, setModelVersion] = useState(0);

  // The roulette entry and the rotation angle follow the server clock.
  const ticking = Boolean((safeRules.schedule && safeRules.schedule.length) || rotating);
  useEffect(() => {
    if (!ticking) return undefined;
    const timer = window.setInterval(() => setTick((n) => n + 1), rotating ? 200 : 500);
    return () => window.clearInterval(timer);
  }, [rotating, ticking]);
  const forced = scheduledEntry(safeRules, serverNow());
  const rotation = rotating ? ((serverNow() / 1000) * (reducedMotion ? 4 : 10)) % 360 : null;

  // ---- pictures that show under this writer's own work
  useEffect(() => {
    const ids = [drawingId, onionId].filter(Boolean);
    ids.forEach((id) => store.request(id));
    const unsubscribers = ids.map((id) => store.subscribe(id, () => setModelVersion((n) => n + 1)));
    return () => unsubscribers.forEach((fn) => fn && fn());
  }, [drawingId, onionId, store]);

  const underlays = useMemo(() => {
    void modelVersion;
    const out = [];
    const own = store.get(drawingId);
    if (own) {
      const baseItems = itemsPicture(own.base, rect);
      if (baseItems) out.push({ key: 'base-items', src: baseItems, left: 0, top: 0, width: 100, height: 100 });
      (own.basePictures || []).forEach((picture) => out.push(placed(picture, rect)));
      // Everyone else drawing on the same picture (a shared mural), live.
      if (shared) (own.pictures || []).filter((picture) => picture.u !== userId).forEach((picture) => out.push(placed(picture, rect)));
    }
    if (onionId) {
      const onion = store.get(onionId);
      if (onion) {
        const onionItems = itemsPicture([...(onion.base || []), ...(onion.items || [])], rect);
        if (onionItems) out.push({ key: 'onion-items', src: onionItems, left: 0, top: 0, width: 100, height: 100, opacity: 0.28 });
        [...(onion.basePictures || []), ...(onion.pictures || [])].forEach((picture) => out.push(placed(picture, rect, { opacity: 0.28 })));
      }
    }
    return out;
  }, [drawingId, modelVersion, onionId, rect, shared, store, userId]);

  // ---- sending the picture
  const sendNow = useCallback(async () => {
    const state = upload.current;
    if (state.inflight) {
      state.dirty = true;
      return state.inflight;
    }
    const api = apiRef.current;
    if (!api || state.closed || !sessionId) return null;
    state.dirty = false;
    const run = (async () => {
      let image = '';
      try {
        const raw = api.exportPicture();
        image = raw ? await preparePicture(raw, Boolean(safeRules.mirror)) : '';
      } catch {
        image = '';
      }
      if (!image) return;
      const record = recordRef.current;
      recordRef.current = emptyRecord();
      seqRef.current += 1;
      writeNumber(`voxemera:party:${key}:seq`, seqRef.current);
      const result = await partyApi(`/api/party/sessions/${encodeURIComponent(sessionId)}/picture`, {
        method: 'POST',
        body: { drawingId, seq: seqRef.current, image, record, actionId: newActionId() },
      }).catch(() => null);
      state.lastAt = Date.now();
      const restore = () => {
        const later = recordRef.current;
        recordRef.current = { strokes: [...record.strokes, ...later.strokes], undos: record.undos + later.undos, clears: record.clears + later.clears };
        state.dirty = true;
      };
      if (!result) {
        restore();
        return;
      }
      if (result.ok) {
        state.sent = true;
        // Another tab or device got further: continue after its sequence number.
        if (result.data.duplicate && Number(result.data.seq) >= seqRef.current) {
          seqRef.current = Number(result.data.seq);
          restore();
        }
        return;
      }
      const code = result.data && result.data.error;
      if (CLOSED_CODES.has(code)) {
        state.closed = true;
        setClosed(true);
      } else if (result.status === 429 || result.status >= 500) {
        restore();
      } else {
        // The server refused this picture (a rule or a bad image): the last accepted one stands.
        setNotice(describePartyError(code));
      }
    })();
    state.inflight = run;
    try {
      await run;
    } finally {
      state.inflight = null;
    }
    if (state.dirty && !state.closed) {
      window.clearTimeout(state.timer);
      state.timer = window.setTimeout(() => sendNow(), live ? UPLOAD_GAP_MS.live : UPLOAD_GAP_MS.quiet);
    }
    return null;
  }, [drawingId, key, live, safeRules.mirror, sessionId, setNotice]);

  const scheduleSend = useCallback(() => {
    const state = upload.current;
    if (state.closed) return;
    state.dirty = true;
    window.clearTimeout(state.timer);
    const gap = (live ? UPLOAD_GAP_MS.live : UPLOAD_GAP_MS.quiet) - (Date.now() - state.lastAt);
    state.timer = window.setTimeout(() => sendNow(), Math.max(live ? UPLOAD_DELAY_MS.live : UPLOAD_DELAY_MS.quiet, gap));
  }, [live, sendNow]);

  useImperativeHandle(ref, () => ({
    // Sends the latest picture and waits for it (used by "Done").
    flush: async () => {
      const state = upload.current;
      window.clearTimeout(state.timer);
      if (state.dirty || !state.sent) await sendNow();
      const started = Date.now();
      while (upload.current.inflight && Date.now() - started < 4000) {
        // eslint-disable-next-line no-await-in-loop
        await upload.current.inflight;
      }
    },
  }), [sendNow]);

  // Leaving the drawing (phase over, another Voxemera area): send what is there and keep a local copy.
  const sendNowRef = useRef(sendNow);
  sendNowRef.current = sendNow;
  useEffect(() => () => {
    const state = upload.current;
    window.clearTimeout(state.timer);
    if (state.dirty && !state.closed) sendNowRef.current();
    if (apiRef.current && apiRef.current.saveLocally) apiRef.current.saveLocally().catch(() => {});
  }, []);

  // Going off screen: send and save the latest state before the editor is put away.
  useEffect(() => {
    if (onScreen) {
      setEngineMounted(true);
      return undefined;
    }
    let cancelled = false;
    const api = apiRef.current;
    Promise.all([
      api && api.saveLocally ? api.saveLocally().catch(() => {}) : null,
      upload.current.dirty && !upload.current.closed ? sendNowRef.current() : null,
    ]).finally(() => {
      if (!cancelled) setEngineMounted(false);
    });
    return () => {
      cancelled = true;
    };
  }, [onScreen]);

  // Each drawing starts with a fresh upload state.
  useEffect(() => {
    upload.current = { inflight: null, dirty: false, timer: 0, lastAt: 0, closed: false, sent: false };
    recordRef.current = emptyRecord();
    setClosed(false);
  }, [key]);

  // ---- engine events: the stroke record and the rules that need the stroke's shape
  const onEngineEvent = useCallback((event, context) => {
    const type = String(event.type || '');
    if (type === 'stroke_start') {
      const family = toolFamily(event.tool || context.tool);
      if (family === '' || family === 'other') {
        strokeRef.current = null;
        return;
      }
      const point = event.point || {};
      strokeRef.current = {
        t: family,
        c: normaliseHex(event.color || event.strokeColor || context.color) || '#000000',
        w: Math.round(Number(event.size || event.brushSize || context.size) || 0),
        points: Number.isFinite(Number(point.x)) ? [[Number(point.x), Number(point.y)]] : [],
      };
      return;
    }
    if (type === 'stroke_point') {
      const stroke = strokeRef.current;
      const point = event.point || {};
      if (stroke && stroke.points.length < 4000 && Number.isFinite(Number(point.x))) stroke.points.push([Number(point.x), Number(point.y)]);
      return;
    }
    if (type !== 'stroke_end') return;
    let stroke = strokeRef.current;
    strokeRef.current = null;
    if (!stroke) {
      // Fills and text commit without a start event.
      const family = toolFamily(event.tool || context.tool);
      if (family !== 'fill' && family !== 'text') return;
      stroke = { t: family, c: normaliseHex(context.color) || '#000000', w: family === 'fill' ? 0 : Math.round(Number(context.size) || 0), points: [] };
    }
    if (safeRules.noStraight && stroke.t === 'pen' && isStraightStroke(stroke.points)) {
      if (apiRef.current) apiRef.current.revertLastStroke();
      setNotice('Straight lines are not allowed: that stroke was removed.');
      return;
    }
    recordRef.current.strokes.push({ t: stroke.t, c: stroke.c, w: stroke.w, ...(safeRules.noStraight && stroke.t === 'pen' ? { p: samplePoints(stroke.points) } : {}) });
    if (stroke.t !== 'fill' && stroke.t !== 'text') {
      usedRef.current += 1;
      writeNumber(`voxemera:party:${key}:strokes`, usedRef.current);
      setStrokesUsed(usedRef.current);
    }
  }, [key, safeRules.noStraight, setNotice]);

  const onEdit = useCallback((kind) => {
    if (kind === 'undo' || kind === 'redo') recordRef.current.undos += 1;
    if (kind === 'clear') recordRef.current.clears += 1;
  }, []);

  const isInputBlocked = useCallback((tool) => {
    if (disabledRef.current || upload.current.closed) return true;
    if (strokeRef.current) return false;
    const family = toolFamily(tool);
    return Boolean(maxStrokes && usedRef.current >= maxStrokes && family !== '' && family !== 'fill');
  }, [maxStrokes]);

  const gameMode = useMemo(() => ({
    key,
    title: label,
    rules: safeRules,
    forced,
    rotation,
    underlays,
    blindLabel: safeRules.blind ? 'Drawing blind 🙈 your strokes stay hidden until the reveal' : '',
    overlay: visual.includes('obscure') ? <div className={`party-obscure${reducedMotion ? ' still' : ''}`}><span /><span /><span /></div> : null,
    apiRef,
    onEngineEvent,
    onEdit,
    onContentChange: scheduleSend,
    isInputBlocked,
  }), [forced, isInputBlocked, key, label, onEdit, onEngineEvent, reducedMotion, rotation, safeRules, scheduleSend, underlays, visual]);

  const canvas = useMemo(() => ({
    id: docId,
    name: label,
    owner_user_id: userId,
    participant_user_ids: [],
    max_participants: 1,
    canvas_width: rect.w,
    canvas_height: rect.h,
    animation_enabled: false,
  }), [docId, label, rect.h, rect.w, userId]);
  const user = useMemo(() => ({ id: userId, username: userName }), [userId, userName]);

  const strokesLeft = maxStrokes ? Math.max(0, maxStrokes - strokesUsed) : null;
  const palette = forced && forced.color ? [forced.color] : safeRules.colors && safeRules.colors.length ? safeRules.colors : null;

  return (
    <div className="party-engine-surface" data-testid="party-surface" data-drawing-id={drawingId} data-engine="canvas">
      {palette || strokesLeft !== null || forced || safeRules.mirror || rect.w !== BOARD.w ? (
        <div className="party-engine-rules" role="status" aria-live="polite">
          {palette ? (
            <span className="party-engine-palette" aria-label="Allowed colours">
              {palette.map((colour) => <span key={colour} className="party-dot" style={{ background: colour }} title={colour} />)}
            </span>
          ) : null}
          {forced ? <span data-testid="roulette">Roulette: {TOOL_NAMES[forced.tool] || forced.tool}{forced.size ? ` · ${forced.size}px` : ''}</span> : null}
          {strokesLeft !== null ? <span data-testid="strokes-left">{strokesLeft} stroke{strokesLeft === 1 ? '' : 's'} left</span> : null}
          {safeRules.mirror ? <span>Mirrored</span> : null}
          {rect.w !== BOARD.w || rect.h !== BOARD.h ? <span>Your part: {rect.w}×{rect.h}</span> : null}
        </div>
      ) : null}
      <div className="party-engine-frame">
        {engineMounted ? (
          <CanvasRoomView
            key={key}
            canvasId={docId}
            canvas={canvas}
            user={user}
            currentUser={user}
            currentUserName={userName || 'Player'}
            gameMode={gameMode}
          />
        ) : <div className="party-empty">Drawing paused while you are elsewhere in Voxemera.</div>}
        {disabled ? <div className="party-engine-done" data-testid="surface-submitted">Submitted ✓</div> : null}
        {closed && !disabled ? <div className="party-engine-done">Time is up for this drawing</div> : null}
      </div>
    </div>
  );
});

export default PartyCanvasSurface;
